import React from 'react';
import { Plus, X, CalendarIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar } from '../ui/calendar';
import { format } from 'date-fns';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

/**
 * A/B Testing Section for subject line OR body variants
 */
export const ABTestingSection = ({
  enabled,
  setEnabled,
  testType = 'subject',
  setTestType,
  subjectVariants,
  setSubjectVariants,
  bodyVariants,
  setBodyVariants,
  testIdPrefix = ''
}) => {
  const variants = testType === 'body' ? bodyVariants : subjectVariants;
  const setVariants = testType === 'body' ? setBodyVariants : setSubjectVariants;

  return (
    <Card className="border-slate-200 bg-blue-50/50">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <span className="text-blue-600 font-bold">A/B</span>
          Testing
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <Checkbox
            id={`${testIdPrefix}ab-test-toggle`}
            checked={enabled}
            onCheckedChange={setEnabled}
            data-testid={`${testIdPrefix}ab-test-toggle`}
          />
          <Label htmlFor={`${testIdPrefix}ab-test-toggle`} className="text-sm cursor-pointer">
            Enable A/B testing to compare different versions
          </Label>
        </div>
        
        {enabled && (
          <div className="space-y-4 pt-2">
            {/* Test Type Selection */}
            <div className="flex items-center gap-3">
              <Label className="text-sm text-slate-600">Test:</Label>
              <Select value={testType} onValueChange={setTestType}>
                <SelectTrigger className="w-48" data-testid={`${testIdPrefix}ab-test-type`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="subject">Subject Lines</SelectItem>
                  <SelectItem value="body">Email Body Content</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <p className="text-xs text-slate-600">
              Recipients will be randomly split between variants. Track open/click rates to find the best performer.
            </p>

            {/* Subject Line Variants */}
            {testType === 'subject' && (
              <div className="space-y-3">
                {subjectVariants.map((variant, idx) => (
                  <div key={`subject-variant-${idx}`} className="flex items-center gap-2">
                    <span className="text-sm font-medium text-blue-600 w-8">
                      {String.fromCharCode(65 + idx)}:
                    </span>
                    <Input
                      value={variant}
                      onChange={(e) => {
                        const newVariants = [...subjectVariants];
                        newVariants[idx] = e.target.value;
                        setSubjectVariants(newVariants);
                      }}
                      placeholder={`Subject line variant ${String.fromCharCode(65 + idx)}`}
                      className="flex-1"
                      data-testid={`${testIdPrefix}subject-variant-${idx}`}
                    />
                    {idx >= 2 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newVariants = subjectVariants.filter((_, i) => i !== idx);
                          setSubjectVariants(newVariants);
                        }}
                        className="text-slate-400 hover:text-red-600"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                {subjectVariants.length < 5 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSubjectVariants([...subjectVariants, ""])}
                    className="text-blue-600 border-blue-300 hover:bg-blue-50"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Subject Variant
                  </Button>
                )}
              </div>
            )}

            {/* Body Variants */}
            {testType === 'body' && (
              <div className="space-y-4">
                <p className="text-xs text-amber-600 font-medium">
                  Note: Body variants use HTML. The template subject line will be used for all variants.
                </p>
                {bodyVariants.map((variant, idx) => (
                  <div key={`body-variant-${idx}`} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-blue-600">
                        Variant {String.fromCharCode(65 + idx)}:
                      </span>
                      {idx >= 2 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newVariants = bodyVariants.filter((_, i) => i !== idx);
                            setBodyVariants(newVariants);
                          }}
                          className="text-slate-400 hover:text-red-600"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <Textarea
                      value={variant}
                      onChange={(e) => {
                        const newVariants = [...bodyVariants];
                        newVariants[idx] = e.target.value;
                        setBodyVariants(newVariants);
                      }}
                      placeholder={`Email body HTML for variant ${String.fromCharCode(65 + idx)}`}
                      rows={4}
                      className="font-mono text-xs"
                      data-testid={`${testIdPrefix}body-variant-${idx}`}
                    />
                  </div>
                ))}
                {bodyVariants.length < 5 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setBodyVariants([...bodyVariants, ""])}
                    className="text-blue-600 border-blue-300 hover:bg-blue-50"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Body Variant
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Schedule Options Section for campaign delivery
 */
export const ScheduleOptionsSection = ({
  scheduleMode,
  setScheduleMode,
  scheduledDate,
  setScheduledDate,
  scheduledTime,
  setScheduledTime,
  testIdPrefix = ''
}) => (
  <Card className="border-slate-200 bg-orange-50/50">
    <CardHeader className="pb-3">
      <CardTitle className="text-base flex items-center gap-2">
        <CalendarIcon className="w-4 h-4" />
        Delivery Options
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="flex gap-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name={`${testIdPrefix}scheduleMode`}
            value="now"
            checked={scheduleMode === "now"}
            onChange={() => setScheduleMode("now")}
            className="w-4 h-4 text-orange-600 border-slate-300 focus:ring-orange-500"
          />
          <span className="text-sm font-medium text-slate-700">Save as draft (send manually)</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name={`${testIdPrefix}scheduleMode`}
            value="scheduled"
            checked={scheduleMode === "scheduled"}
            onChange={() => setScheduleMode("scheduled")}
            className="w-4 h-4 text-orange-600 border-slate-300 focus:ring-orange-500"
          />
          <span className="text-sm font-medium text-slate-700">Schedule for later</span>
        </label>
      </div>

      {scheduleMode === "scheduled" && (
        <div className="flex gap-4 items-end pt-2">
          <div className="flex-1">
            <Label className="text-sm">Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={`w-full justify-start text-left font-normal mt-1 ${!scheduledDate && "text-muted-foreground"}`}
                  data-testid={`${testIdPrefix}schedule-date-btn`}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {scheduledDate ? format(scheduledDate, "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={scheduledDate}
                  onSelect={setScheduledDate}
                  disabled={(date) => date < new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className="w-32">
            <Label className="text-sm">Time</Label>
            <Input
              type="time"
              value={scheduledTime}
              onChange={(e) => setScheduledTime(e.target.value)}
              className="mt-1"
              data-testid={`${testIdPrefix}schedule-time-input`}
            />
          </div>
        </div>
      )}
    </CardContent>
  </Card>
);

export default {
  ABTestingSection,
  ScheduleOptionsSection
};
